import { useState, useEffect, useCallback } from 'react';
import { endAdminSession } from '../lib/adminSession';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/secure-files`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

export interface FileCategory {
  id: string;
  name: string;
  description: string;
  order_index: number;
  created_at: string;
  total_downloads: number;
  file_count: number;
}

export interface FileItem {
  id: string;
  category_id: string;
  name: string;
  description: string;
  file_size: number;
  update_date: string | null;
  download_count: number;
  order_index: number;
  has_password: boolean;
  original_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface FileDownloadLog {
  file_item_id: string;
  downloaded_at: string;
}

export interface ManagedFilePassword {
  id: string;
  name: string;
  category_id: string;
  password_plain: string | null;
}

export interface CustomPassword {
  id: string;
  label: string;
  description: string;
  password_plain: string;
  created_at: string;
  updated_at: string;
}

export interface PasswordsOverview {
  files: ManagedFilePassword[];
  custom: CustomPassword[];
}

export interface CustomPasswordInput {
  label: string;
  description: string;
  password: string;
}

export interface AdminStats {
  items: { id: string; name: string; download_count: number; category_id: string }[];
  recentDownloads: FileDownloadLog[];
}

function getAdminSecret(): string {
  return localStorage.getItem('admin_token') || '';
}

function authHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${ANON_KEY}`,
    'Content-Type': 'application/json',
  };
}

function adminHeaders(): Record<string, string> {
  return {
    Authorization: `Bearer ${ANON_KEY}`,
    'X-Admin-Token': getAdminSecret(),
  };
}

export function useSupabaseFiles() {
  const [categories, setCategories] = useState<FileCategory[]>([]);
  const [items, setItems] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${FUNCTION_URL}/categories`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      setCategories(data.categories ?? []);
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  const fetchItems = useCallback(async (categoryId?: string) => {
    try {
      const url = categoryId
        ? `${FUNCTION_URL}/items?category_id=${categoryId}`
        : `${FUNCTION_URL}/items`;
      const res = await fetch(url, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Failed: ${res.status}`);
      const data = await res.json();
      setItems(data.items ?? []);
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([fetchCategories(), fetchItems()]);
    setLoading(false);
  }, [fetchCategories, fetchItems]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const verifyPassword = useCallback(async (fileId: string, password: string): Promise<{ url?: string; blob?: Blob; filename?: string; error?: string }> => {
    try {
      const res = await fetch(`${FUNCTION_URL}/verify`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ file_id: fileId, password }),
      });

      const contentType = res.headers.get('Content-Type') ?? '';
      if (contentType.includes('application/json')) {
        const data = await res.json();
        if (!res.ok) return { error: data.error ?? 'Failed' };
        return { url: data.url };
      }

      if (!res.ok) return { error: 'Failed' };
      const blob = await res.blob();

      const disposition = res.headers.get('Content-Disposition') ?? '';
      const filenameMatch = disposition.match(/filename="([^"]+)"/);
      const filename = filenameMatch ? filenameMatch[1] : undefined;

      return { blob, filename };
    } catch (err: any) {
      return { error: err.message };
    }
  }, []);

  // Admin: categories
  const createCategory = useCallback(async (name: string, description: string, orderIndex: number) => {
    const res = await fetch(`${FUNCTION_URL}/admin/category`, {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify({ name, description, order_index: orderIndex }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    await fetchCategories();
    return data.category;
  }, [fetchCategories]);

  const updateCategory = useCallback(async (id: string, updates: { name?: string; description?: string; order_index?: number }) => {
    const res = await fetch(`${FUNCTION_URL}/admin/category/${id}`, {
      method: 'PUT',
      headers: adminHeaders(),
      body: JSON.stringify(updates),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    await fetchCategories();
    return data.category;
  }, [fetchCategories]);

  const deleteCategory = useCallback(async (id: string) => {
    const res = await fetch(`${FUNCTION_URL}/admin/category/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    await fetchCategories();
    await fetchItems();
  }, [fetchCategories, fetchItems]);

  // Request a presigned PUT URL from the edge function, then upload the file
  // bytes directly from the browser to R2. R2 handles large single uploads (up
  // to 5GB), so no chunking is needed. The browser MUST send the exact
  // content-type the URL was signed with, or R2 rejects the PUT with 403.
  const uploadFileToR2 = useCallback(async (
    categoryId: string,
    file: File,
    onProgress?: (percent: number) => void
  ): Promise<string> => {
    const requestedContentType = file.type || 'application/octet-stream';

    const urlRes = await fetch(`${FUNCTION_URL}/admin/upload-url`, {
      method: 'POST',
      headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        category_id: categoryId,
        file_name: file.name,
        file_size: file.size,
        content_type: requestedContentType,
      }),
    });
    const urlData = await urlRes.json().catch(() => ({}));
    if (urlRes.status === 401) {
      endAdminSession();
      throw new Error('Your admin session has ended. Please log in again to upload files.');
    }
    if (!urlRes.ok) throw new Error(urlData.error || 'Failed to get upload URL');
    const info = urlData as { path: string; url: string; content_type: string };
    if (!info.url || !info.path) throw new Error('Invalid upload response from server');
    const signedContentType = info.content_type || requestedContentType;

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', info.url);
      xhr.setRequestHeader('Content-Type', signedContentType);
      xhr.timeout = 10.5 * 60 * 1000;

      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (onProgress) onProgress(100);
          resolve();
        } else {
          reject(new Error(`Upload failed (HTTP ${xhr.status})`));
        }
      };
      xhr.onerror = () => {
        reject(new Error(
          'Network error during upload to R2 — this usually means the bucket ' +
          'does not exist yet, CORS is not applied, or the presigned URL expired.'
        ));
      };
      xhr.ontimeout = () => reject(new Error('Upload timed out'));
      xhr.send(file);
    });

    return info.path;
  }, []);

  const createItem = useCallback(async (data: {
    name: string;
    description: string;
    categoryId: string;
    password: string;
    updateDate: string;
    order: number;
    file: File | null;
  }, onProgress?: (percent: number) => void) => {
    let filePath = '';
    let fileSize = 0;
    let originalName: string | undefined;

    if (data.file) {
      filePath = await uploadFileToR2(data.categoryId, data.file, onProgress);
      fileSize = data.file.size;
      originalName = data.file.name;
    }

    const res = await fetch(`${FUNCTION_URL}/admin/item`, {
      method: 'POST',
      headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: data.name,
        description: data.description,
        category_id: data.categoryId,
        password: data.password || undefined,
        update_date: data.updateDate || undefined,
        order_index: data.order,
        file_path: filePath,
        file_size: fileSize,
        original_name: originalName,
        storage_type: 'r2',
      }),
    });
    const resp = await res.json();
    if (!res.ok) throw new Error(resp.error || 'Failed');
    await fetchItems();
    await fetchCategories();
    return resp.item;
  }, [fetchItems, fetchCategories, uploadFileToR2]);

  const updateItem = useCallback(async (id: string, data: {
    name: string;
    description: string;
    categoryId: string;
    password: string;
    updateDate: string;
    order: number;
    file: File | null;
  }, onProgress?: (percent: number) => void) => {
    let filePath = '';
    let fileSize = 0;
    let originalName: string | undefined;

    if (data.file) {
      filePath = await uploadFileToR2(data.categoryId, data.file, onProgress);
      fileSize = data.file.size;
      originalName = data.file.name;
    }

    const res = await fetch(`${FUNCTION_URL}/admin/item/${id}`, {
      method: 'PUT',
      headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: data.name,
        description: data.description,
        password: data.password || undefined,
        update_date: data.updateDate || undefined,
        order_index: data.order,
        file_path: filePath || undefined,
        file_size: fileSize || undefined,
        original_name: originalName,
        storage_type: filePath ? 'r2' : undefined,
      }),
    });
    const resp = await res.json();
    if (!res.ok) throw new Error(resp.error || 'Failed');
    await fetchItems();
    return resp.item;
  }, [fetchItems, uploadFileToR2]);

  const deleteItem = useCallback(async (id: string) => {
    const res = await fetch(`${FUNCTION_URL}/admin/item/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error);
    await fetchItems();
    await fetchCategories();
  }, [fetchItems, fetchCategories]);

  const listPasswords = useCallback(async (): Promise<PasswordsOverview> => {
    const res = await fetch(`${FUNCTION_URL}/admin/passwords`, { headers: adminHeaders() });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Failed to load passwords');
    return { files: data.files ?? [], custom: data.custom ?? [] };
  }, []);

  const saveCustomPassword = useCallback(async (id: string | null, input: CustomPasswordInput): Promise<CustomPassword> => {
    const res = await fetch(id ? `${FUNCTION_URL}/admin/password/${id}` : `${FUNCTION_URL}/admin/password`, {
      method: id ? 'PUT' : 'POST',
      headers: { ...adminHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Failed to save password');
    return data.password;
  }, []);

  const deleteCustomPassword = useCallback(async (id: string) => {
    const res = await fetch(`${FUNCTION_URL}/admin/password/${id}`, {
      method: 'DELETE',
      headers: adminHeaders(),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Failed to delete password');
  }, []);

  const getAdminStats = useCallback(async (): Promise<AdminStats | null> => {
    const res = await fetch(`${FUNCTION_URL}/admin/stats`, { headers: adminHeaders() });
    if (!res.ok) return null;
    const data = await res.json();
    return { items: data.items ?? [], recentDownloads: data.recentDownloads ?? [] };
  }, []);

  return {
    categories,
    items,
    loading,
    error,
    fetchAll,
    fetchItems,
    verifyPassword,
    createCategory,
    updateCategory,
    deleteCategory,
    createItem,
    updateItem,
    deleteItem,
    getAdminStats,
    listPasswords,
    saveCustomPassword,
    deleteCustomPassword,
  };
}
